import { AppConfiguration, IAppConfiguration, IInitParams, loadAppConfiguration } from "Configuration";
import { IUser } from "contracts";

import { LoggingSection } from "@multiweb/logging";
import { PromiseUtils } from "Global/Base/Promises/PromiseUtils";
import { UrlUtils } from "Global/Base/Utils/UrlUtils";
import { createDefaultSectionLoggerFactory } from "Logging/defaultLogger";
import { IUserSettingsManager } from "OW.Remoting/Remoting/IUserSettingsManager";
import { CaseInsensitiveMap } from "../Global/Base/DataTypes/CaseInsensitiveMap";
import { ContextType, GlobalContext } from "../Global/Base/GlobalContext";
import { ApplicationInfo } from "./ApplicationInfo";
import { IAppAddresses } from "./IAppAddresses";
import { IAppContext } from "./IAppContext";

// KOPIE pro skripty ve script-utils.js - při změně nutno synchronizovat!
const BASE_PAGES_REGEX = /(default|login|logout|main|form)(\.html|\/$|$|\?.*)/i;

/**
 * Kontext s informacemi o aplikaci. Některé informace jsou lokální pro každé okno, některé jsou sdílené napříč všemi okny.
 * @includeToDoc
 */
export class AppContext implements IAppContext {
    private static _instance?: AppContext;

    private _configuration: IAppConfiguration;
    private _appInfo: ApplicationInfo;
    private _basePages: RegExp = BASE_PAGES_REGEX;
    private _currentUser: IUser | null = null;
    private _initialized = false;
    private _initializeDeferedPromise = PromiseUtils.defer<void>();

    public activeUserRolesIds: string[] = [];

    public readonly addressParams: CaseInsensitiveMap<string> = new CaseInsensitiveMap<string>();

    public get currentUser(): IUser | null {
        return this._currentUser;
    }

    public async waitForInitialized(): Promise<void> {
        return this._initializeDeferedPromise.promise;
    }

    public set basePages(v: RegExp | undefined) {
        this._basePages = v || BASE_PAGES_REGEX;
    }

    public get configuration(): IAppConfiguration {
        return this._configuration;
    }

    /**
     * Instance s kontextem aplikace
     */
    public static get instance(): IAppContext {
        if (AppContext._instance === undefined)
            AppContext._instance = new AppContext();

        return AppContext._instance;
    }

    public get appId(): string {
        return this._appInfo.appId;
    }

    public get addresses(): IAppAddresses {
        return this._appInfo;
    }

    public async initialize(initParams?: Partial<IInitParams>): Promise<void> {
        // tato metoda se volá pro inicializaci v každém okně zvlášť, protože každý iframe má vlastní konektory,
        // které se napojují na workery(např.Localization, OWManagerCommunication) a kažý má vlastní url

        if (this._initialized)
            return;

        // konfigurace je sdílená pro všechna okna, takže ji stačí načítat pouze jednou
        this._configuration = new AppConfiguration(initParams || await loadAppConfiguration());

        const logger = createDefaultSectionLoggerFactory(this._configuration.logging).create(LoggingSection.app);

        const configServiceUrl = this._configuration.webServiceUrl;
        try {
            const clientUrl = new URL(location.href.replace(this._basePages, ""));
            const serviceUrl = UrlUtils.parseUrl(configServiceUrl, clientUrl);
            this._appInfo = new ApplicationInfo(clientUrl, serviceUrl);

            if (serviceUrl) {
                logger.debug(`Communicating with service: '${serviceUrl}'`);

                if (serviceUrl.host !== clientUrl.host)
                    logger.error(`Trying to communicate with service '${serviceUrl}' on domain different from client's domain. This is blocked by CORS policy.`);
            }
            this._initializeDeferedPromise.resolve();
        } catch (e) {
            this._initializeDeferedPromise.reject();
            logger.error(`Failed to parse URL from configuration parameter 'webServiceUrl' ('${configServiceUrl}')`);
            throw e;
        }

        this._initialized = true;

        this._loadUrlParams();
    }

    private _loadUrlParams(): void {
        // načtení parametru z adresy
        let addrParam = location.search;
        if (addrParam.length > 0)
            addrParam = addrParam.substr(1);

        const pairs = addrParam.split("&");
        const usedPairName = new Array<string>();

        for (const pair of pairs) {
            const param = pair.split("=");
            if (usedPairName.includes(param[0]))
                continue;

            const values = new URLSearchParams(location.search).getAll(param[0]);

            if (param.length > 0 && param[0] !== "") {
                this.addressParams.set(param[0].toLowerCase(), values.toString());
                usedPairName.push(param[0]);
            }
        }
    }

    public setCurrentUser(user: IUser): void {
        this._currentUser = user;
    }

    public setActiveUserRolesIds(ids: string[]): void {
        this.activeUserRolesIds = ids;
    }

    public async setActiveUserRolesRanges(userSettingsManager: IUserSettingsManager): Promise<void> {
        userSettingsManager.setActiveUserRoleRanges();
    }
}

GlobalContext.registerType(AppContext, ContextType.current);
